import React, { useState } from 'react';
import { useToast } from './toast';
import { isInviteBlocked, availabilityLabel } from './lib/availability';
import { AvailabilityBadge } from './components/AvailabilityCalendar';

export const HireModal = ({ freelancer, jobs = [], onClose, onSent, initial = {} }) => {
  const blockedByVacation = isInviteBlocked(freelancer?.availability);
  const [message, setMessage] = useState(initial.message || 'Hi! I\'d like to discuss a project with you.');
  const [contractType, setContractType] = useState(initial.contractType || 'hourly');
  const [amount, setAmount] = useState(initial.amount || '');
  const [selectedJobId, setSelectedJobId] = useState(initial.jobId || '');
  const [backendJobId, setBackendJobId] = useState(initial.backendJobId || (new URLSearchParams(window.location.search)).get('jobId') || '');
  const [sending, setSending] = useState(false);
  const [error, setError] = useState('');
  const [confirmation, setConfirmation] = useState(null);
  const { addToast } = useToast();

  const resetErrors = () => setError('');

  const submit = async () => {
    resetErrors();

    if (blockedByVacation) {
      const label = availabilityLabel(freelancer?.availability);
      setError(`${freelancer?.name || 'This freelancer'} is unavailable: ${label}.`);
      addToast({ type: 'error', message: `Invites paused — ${label}.` });
      return;
    }

    // Basic validation
    if (!message.trim()) {
      setError('Please add a brief message to the freelancer.');
      addToast({ type: 'error', message: 'Message is required.' });
      return;
    }
    if (contractType === 'hourly' && amount && Number(amount) < 0) {
      setError('Hourly rate cannot be negative.');
      addToast({ type: 'error', message: 'Hourly rate cannot be negative.' });
      return;
    }
    if (contractType === 'fixed' && amount && Number(amount) < 0) {
      setError('Budget cannot be negative.');
      addToast({ type: 'error', message: 'Budget cannot be negative.' });
      return;
    }

    // update URL params for shareable invite state
    const params = new URLSearchParams(window.location.search);
    const jid = backendJobId || selectedJobId;
    if (jid) params.set('jobId', jid); else params.delete('jobId');
    if (contractType) params.set('ctype', contractType); else params.delete('ctype');
    if (amount) params.set('amt', String(amount)); else params.delete('amt');
    const newUrl = `${window.location.pathname}?${params.toString()}`;
    window.history.replaceState({}, '', newUrl);

    // Attempt backend wiring to create proposal (if a job is selected)
    const backendUrl = process.env.REACT_APP_BACKEND_URL;
    const token = localStorage.getItem('token');
    const effectiveJobId = backendJobId || selectedJobId;
    const canCallBackend = Boolean(backendUrl && token && effectiveJobId);

    setSending(true);
    try {
      // small UX delay
      await new Promise(r => setTimeout(r, 250));

      let backendError = '';
      let createdProposal = null;

      if (canCallBackend) {
        try {
          const proposalPayload = {
            job_id: String(effectiveJobId),
            cover_letter: message,
            proposed_rate: contractType === 'hourly' ? (amount ? parseFloat(amount) : undefined) : undefined,
            proposed_budget: contractType === 'fixed' ? (amount ? parseFloat(amount) : undefined) : undefined,
            milestones: [],
            question_responses: {}
          };

          const res = await fetch(`${backendUrl}/api/v1/proposals/`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${token}`
            },
            body: JSON.stringify(proposalPayload)
          });

          if (!res.ok) {
            backendError = `Invite sent, but creating a proposal on the server failed (status ${res.status}).`;
          } else {
            createdProposal = await res.json();
          }
        } catch (err) {
          backendError = 'Invite sent, but we could not reach the server to create a proposal.';
          console.error('Error creating proposal', err);
        }
      }

      // Call consumer callback
      const payload = { 
        freelancerId: freelancer?.id, 
        jobId: selectedJobId || null, 
        contractType, 
        amount: amount ? parseFloat(amount) : undefined, 
        message,
        proposal: createdProposal || null
      };
      onSent && onSent(payload);

      // Show confirmation sheet in-modal instead of alert
      const jobTitle = jobs.find(j => String(j.id) === String(selectedJobId))?.title;
      setConfirmation({
        freelancerName: freelancer?.name || 'the freelancer',
        jobId: effectiveJobId || null,
        jobTitle: jobTitle || (backendJobId ? '(Backend Job)' : null),
        contractType,
        amount: amount ? parseFloat(amount) : undefined,
        message,
        backendError: backendError || ''
      });

      if (backendError) {
        addToast({ type: 'warning', message: backendError });
      } else {
        addToast({ type: 'success', message: 'Invite sent successfully.' });
      }
    } finally {
      setSending(false);
    }
  };

  const renderForm = () => (
    <>
      {error && (
        <div className="mx-6 mt-4 px-3 py-2 bg-red-50 text-red-700 border border-red-200 rounded-lg text-sm">
          {error}
        </div>
      )}
      <div className="p-6 space-y-4">
        <div>
          <label className="block text-sm text-gray-700 mb-1">Invite to job (optional)</label>
          <select value={selectedJobId} onChange={(e)=>setSelectedJobId(e.target.value)} className="w-full px-3 py-2 border border-gray-300 rounded-lg">
            <option value="">— Select Job —</option>
            {jobs.map(j => (
              <option key={j.id} value={j.id}>{j.title}</option>
            ))}
          </select>
        </div>
        <div>
          <label className="block text-sm text-gray-700 mb-1">Message *</label>
          <textarea value={message} onChange={(e)=>setMessage(e.target.value)} className="w-full h-24 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gray-500 focus:border-gray-500" />
        </div>
        <div className="flex items-start gap-3 flex-col sm:flex-row">
          <div className="flex items-center gap-3">
            <label className="text-sm text-gray-700">Contract Type:</label>
            <select value={contractType} onChange={(e)=>setContractType(e.target.value)} className="px-3 py-2 border border-gray-300 rounded-lg">
              <option value="hourly">Hourly</option>
              <option value="fixed">Fixed-price</option>
            </select>
          </div>
          <div className="flex-1 w-full">
            <label className="block text-sm text-gray-700 mb-1">Backend Job ID (Mongo ObjectId)</label>
            <input type="text" value={backendJobId} onChange={(e)=>setBackendJobId(e.target.value)} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gray-500 focus:border-gray-500" placeholder="Paste real backend job _id (24 hex)" />
            <p className="text-xs text-gray-500 mt-1">Optional. Overrides the dropdown above for backend invite.</p>
          </div>
        </div>
        <div>
          <label className="block text-sm text-gray-700 mb-1">{contractType==='hourly' ? 'Proposed Hourly Rate (USD)' : 'Proposed Budget (USD)'}</label>
          <input type="number" min="0" step="0.01" value={amount} onChange={(e)=>setAmount(e.target.value)} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gray-500 focus:border-gray-500" placeholder={contractType==='hourly' ? 'e.g., 60' : 'e.g., 2000'} />
        </div>
      </div>
      <div className="px-6 py-4 border-t border-gray-200 bg-gray-50 flex justify-between items-center">
        <button onClick={onClose} className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-100">Cancel</button>
        <button disabled={sending} onClick={submit} className="bg-black text-white px-6 py-2 rounded-lg hover:bg-gray-800 font-semibold disabled:opacity-60">{sending ? 'Sending…' : 'Send Invite'}</button>
      </div>
    </>
  );

  const renderConfirmation = () => (
    <>
      <div className="p-6 space-y-4">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-full bg-black text-white flex items-center justify-center font-bold">
            {String(freelancer?.name || 'F')[0]}
          </div>
          <div>
            <div className="text-lg font-semibold text-gray-900">Invite sent</div>
            <div className="text-sm text-gray-600">to {confirmation?.freelancerName}</div>
          </div>
        </div>

        <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 text-sm text-gray-800">
          {confirmation?.jobTitle ? (
            <div className="mb-2"><span className="text-gray-500">Job:</span> {confirmation.jobTitle}</div>
          ) : (
            <div className="mb-2"><span className="text-gray-500">Job:</span> Not specified</div>
          )}
          <div className="mb-2"><span className="text-gray-500">Contract:</span> {confirmation?.contractType === 'hourly' ? 'Hourly' : 'Fixed-price'}{confirmation?.amount ? ` • $${confirmation.amount}` : ''}</div>
          <div className="mb-1"><span className="text-gray-500">Message:</span></div>
          <div className="whitespace-pre-wrap">{confirmation?.message}</div>
        </div>

        {confirmation?.backendError && (
          <div className="px-3 py-2 bg-yellow-50 text-yellow-800 border border-yellow-200 rounded-lg text-sm">
            {confirmation.backendError}
          </div>
        )}
      </div>
      <div className="px-6 py-4 border-t border-gray-200 bg-gray-50 flex justify-between items-center">
        <button onClick={onClose} className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-100">Close</button>
        {confirmation?.jobId && (
          <a href={`/jobs/${confirmation.jobId}`} className="bg-black text-white px-6 py-2 rounded-lg hover:bg-gray-800 font-semibold">View Job</a>
        )}
      </div>
    </>
  );

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md mx-4 overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-200 flex justify-between items-center">
          <div className="flex items-center gap-2">
            <h3 className="text-lg font-semibold text-gray-900">{confirmation ? 'Invite Sent' : `Invite ${freelancer?.name}`}</h3>
            {!confirmation && <AvailabilityBadge availability={freelancer?.availability} />}
          </div>
          <button onClick={onClose} className="text-gray-500 hover:text-black">✕</button>
        </div>
        {!confirmation && blockedByVacation && (
          <div className="mx-6 mt-4 px-4 py-3 border border-rose-200 bg-rose-50 rounded-lg text-sm text-rose-800">
            {freelancer?.name || 'This freelancer'} is {availabilityLabel(freelancer?.availability).toLowerCase()}.
            They&apos;ll be back automatically — try again then or send a message.
          </div>
        )}
        {confirmation ? renderConfirmation() : renderForm()}
      </div>
    </div>
  );
};

export default { HireModal };